/**
 * TrustGrid Controls List - Enhanced with filtering, grouping, and bulk actions
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Select, Tag, Button, Card, Collapse, Space, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  GroupOutlined
} from '@ant-design/icons';
import { listControls, getControlResults, getControlProjects } from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import { TGDataTable, LoadingState } from '../../../components/common';
import { showError } from '../../../utils/notifications';
import { colors, spacing } from '../../../theme/designTokens';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { Option } = Select;
const { Panel } = Collapse;

// Regulation frameworks for filtering
const REGULATION_FRAMEWORKS = [
  { value: 'GDPR', label: 'GDPR', color: 'blue' },
  { value: 'EU AI Act', label: 'EU AI Act', color: 'purple' },
  { value: 'HIPAA', label: 'HIPAA', color: 'green' },
  { value: 'NIST AI RMF', label: 'NIST AI RMF', color: 'orange' },
  { value: 'ISO 27001', label: 'ISO 27001', color: 'cyan' },
  { value: 'ISO 42001', label: 'ISO 42001', color: 'geekblue' },
  { value: 'SOC 2', label: 'SOC 2', color: 'gold' },
  { value: 'CCPA', label: 'CCPA', color: 'lime' }
];

// Control domains for filtering
const CONTROL_DOMAINS = [
  'Data Governance',
  'Model Governance',
  'Model Performance & Risk',
  'Human Oversight',
  'Operational Resilience'
];

function Controls() {
  const { selectedProjectId, selectProject } = useTrustGrid();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Controls', path: '/trustgrid/controls' }];
  }, [location.state]);

  const [controls, setControls] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [projectCounts, setProjectCounts] = useState({});

  // External filters (not inline column filters)
  const [regulationFilter, setRegulationFilter] = useState([]);
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'regulation', 'severity', 'status', 'domain'

  // Multi-select
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    setSelectedRowKeys([]);  // Clear stale selections from previous project
    fetchData();
  }, [selectedProjectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const controlsRes = await listControls();
      const controlsData = controlsRes.data.controls || [];

      // Fetch project counts for each control
      const projectCountPromises = controlsData.map(control =>
        getControlProjects(control.code)
          .then(res => ({ code: control.code, count: (res.data.projects || []).length }))
          .catch(() => ({ code: control.code, count: 0 }))
      );
      const projectCountsData = await Promise.all(projectCountPromises);
      const countsMap = {};
      projectCountsData.forEach(item => {
        countsMap[item.code] = item.count;
      });
      setProjectCounts(countsMap);

      // Fetch results for specific project or skip if All Projects
      let resultsMap = {};
      if (selectedProjectId) {
        const resultsRes = await getControlResults(selectedProjectId).catch(() => ({ data: { results: [] } }));
        (resultsRes.data.results || []).forEach(r => {
          resultsMap[r.control_code] = r;
        });
      }

      const enriched = controlsData.map(c => ({
        ...c,
        status: resultsMap[c.code]?.status || 'unknown',
        confidence: resultsMap[c.code]?.confidence || 0,
        projectCount: countsMap[c.code] || 0,
        // Extract regulation frameworks from regulation_data
        regulations: extractRegulations(c.regulation_data)
      }));

      setControls(enriched);
      setResults(resultsMap);
    } catch (error) {
      // SECURITY: Handle cross-org access silently
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Controls');
      if (!wasHandled) {
        console.error('Failed to fetch controls:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const extractRegulations = (regulationData) => {
    if (!regulationData || !regulationData.regulations) return [];
    const frameworks = new Set();
    regulationData.regulations.forEach(reg => {
      if (reg.framework) {
        frameworks.add(reg.framework);
      }
    });
    return Array.from(frameworks);
  };

  // Apply external filters (regulation filter only - Status, Severity, Domain now inline)
  const filteredControls = useMemo(() => {
    let filtered = controls;

    // Regulation filter (external multi-select)
    if (regulationFilter.length > 0) {
      filtered = filtered.filter(c => {
        return c.regulations.some(reg => regulationFilter.includes(reg));
      });
    }

    return filtered;
  }, [controls, regulationFilter]);

  // Group controls
  const groupedControls = useMemo(() => {
    if (groupBy === 'none') {
      return { 'All Controls': filteredControls };
    }

    const groups = {};

    if (groupBy === 'regulation') {
      filteredControls.forEach(control => {
        if (control.regulations.length === 0) {
          if (!groups['No Regulations']) groups['No Regulations'] = [];
          groups['No Regulations'].push(control);
        } else {
          control.regulations.forEach(reg => {
            if (!groups[reg]) groups[reg] = [];
            groups[reg].push(control);
          });
        }
      });
    } else if (groupBy === 'severity') {
      filteredControls.forEach(control => {
        const key = control.severity || 'Unknown';
        if (!groups[key]) groups[key] = [];
        groups[key].push(control);
      });
    } else if (groupBy === 'status') {
      filteredControls.forEach(control => {
        const key = control.status || 'unknown';
        if (!groups[key]) groups[key] = [];
        groups[key].push(control);
      });
    } else if (groupBy === 'domain') {
      filteredControls.forEach(control => {
        const key = control.domain || 'No Domain';
        if (!groups[key]) groups[key] = [];
        groups[key].push(control);
      });
    }

    return groups;
  }, [filteredControls, groupBy]);

  // Generate dynamic filter options
  const statusOptions = useMemo(() => {
    const statuses = [...new Set(controls.map(c => c.status))].filter(Boolean).sort();
    return statuses.map(s => ({ label: s.charAt(0).toUpperCase() + s.slice(1), value: s }));
  }, [controls]);

  const severityOptions = useMemo(() => {
    const severities = [...new Set(controls.map(c => c.severity))].filter(Boolean).sort();
    return severities.map(s => ({ label: s.charAt(0).toUpperCase() + s.slice(1), value: s }));
  }, [controls]);

  const domainOptions = useMemo(() => {
    const domains = [...new Set(controls.map(c => c.domain))].filter(Boolean).sort();
    return domains.map(d => ({ label: d, value: d }));
  }, [controls]);

  const columns = [
    {
      title: 'Control',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      fixed: 'left',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      title: 'Control Name',
      dataIndex: 'title',
      key: 'title',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status) => {
        if (status === 'pass') return <CheckCircleOutlined style={{ color: colors.status.pass, fontSize: '18px' }} />;
        if (status === 'fail') return <CloseCircleOutlined style={{ color: colors.status.fail, fontSize: '18px' }} />;
        return <QuestionCircleOutlined style={{ color: colors.text.disabled, fontSize: '18px' }} />;
      },
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: statusOptions,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      render: (severity) => {
        const colorMap = {
          critical: colors.severity.critical.main,
          high: colors.severity.high.main,
          medium: colors.severity.medium.main,
          low: colors.severity.low.main
        };
        return <Tag color={colorMap[severity] || 'default'}>{severity}</Tag>;
      },
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: severityOptions,
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      width: 180,
      render: (domain) => domain ? <Tag color={colors.primary[500]}>{domain}</Tag> : <Tag color="default">No Domain</Tag>,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: domainOptions,
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 120,
      render: (confidence) => `${confidence}%`,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      title: 'Projects',
      dataIndex: 'projectCount',
      key: 'projectCount',
      width: 120,
      align: 'center',
      render: (count) => (
        <Tooltip title={`Used in ${count} project${count !== 1 ? 's' : ''}`}>
          <Tag color={count > 0 ? colors.primary[500] : 'default'}>
            {count} {count === 1 ? 'Project' : 'Projects'}
          </Tag>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      title: 'Regulations',
      dataIndex: 'regulations',
      key: 'regulations',
      width: 200,
      render: (regulations) => (
        <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap' }}>
          {regulations.slice(0, 3).map(reg => {
            const framework = REGULATION_FRAMEWORKS.find(f => f.value === reg);
            return (
              <Tag key={reg} color={framework?.color || 'default'} style={{ fontSize: '11px', margin: 0 }}>
                {reg}
              </Tag>
            );
          })}
          {regulations.length > 3 && (
            <Tag color="default" style={{ fontSize: '11px', margin: 0 }}>
              +{regulations.length - 3}
            </Tag>
          )}
        </div>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk action: ${action} on`, selectedRowKeys);
    // Implement bulk enable/disable here
  };

  const clearExternalFilters = () => {
    setRegulationFilter([]);
  };

  const externalFilterCount = regulationFilter.length > 0 ? 1 : 0;

  return (
    <TrustGridPage
      title="Controls"
      subtitle="Manage and monitor TrustGrid security controls"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchData}
      actions={
        selectedRowKeys.length > 0 && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button onClick={() => handleBulkAction('enable')}>
              Enable Selected ({selectedRowKeys.length})
            </Button>
            <Button onClick={() => handleBulkAction('disable')}>
              Disable Selected ({selectedRowKeys.length})
            </Button>
          </div>
        )
      }
    >
      <ProjectSelector />

      {loading ? (
        <LoadingState type="spinner" />
      ) : groupBy === 'none' ? (
        <TGDataTable
          data={filteredControls}
          columns={columns}
          rowKey="code"
          loading={loading}
          pagination="client"
          pageSize={10}
          showExport
          showColumnConfig
          showFilters
          exportFilename="controls"
          presetKey="trustgrid.controls"
          onRefresh={fetchData}
          rowSelection={rowSelection}
          externalFilterCount={externalFilterCount}
          onClearExternalFilters={clearExternalFilters}
          onRow={(record) => ({
            onClick: (e) => {
              // Don't navigate if clicking checkbox
              if (e.target.type !== 'checkbox') {
                navigate(`/trustgrid/controls/${record.code}`);
              }
            },
            style: { cursor: 'pointer' }
          })}
          toolbar={
            <Space size={spacing.sm}>
              <Select
                mode="multiple"
                value={regulationFilter}
                onChange={setRegulationFilter}
                style={{ width: 300 }}
                placeholder="Filter by regulation framework"
                maxTagCount={2}
              >
                {REGULATION_FRAMEWORKS.map(framework => (
                  <Option key={framework.value} value={framework.value}>
                    <Tag color={framework.color} style={{ margin: 0 }}>{framework.label}</Tag>
                  </Option>
                ))}
              </Select>
              <Select
                value={groupBy}
                onChange={setGroupBy}
                style={{ width: 180 }}
                placeholder="Group by"
                suffixIcon={<GroupOutlined />}
              >
                <Option value="none">No Grouping</Option>
                <Option value="domain">By Domain</Option>
                <Option value="regulation">By Regulation</Option>
                <Option value="severity">By Severity</Option>
                <Option value="status">By Status</Option>
              </Select>
            </Space>
          }
        />
      ) : (
        <Card style={{ marginBottom: spacing.md }}>
          <Space size={spacing.sm} style={{ marginBottom: spacing.md }}>
            <Select
              mode="multiple"
              value={regulationFilter}
              onChange={setRegulationFilter}
              style={{ width: 300 }}
              placeholder="Filter by regulation framework"
              maxTagCount={2}
            >
              {REGULATION_FRAMEWORKS.map(framework => (
                <Option key={framework.value} value={framework.value}>
                  <Tag color={framework.color} style={{ margin: 0 }}>{framework.label}</Tag>
                </Option>
              ))}
            </Select>
            <Select
              value={groupBy}
              onChange={setGroupBy}
              style={{ width: 180 }}
              placeholder="Group by"
              suffixIcon={<GroupOutlined />}
            >
              <Option value="none">No Grouping</Option>
              <Option value="domain">By Domain</Option>
              <Option value="regulation">By Regulation</Option>
              <Option value="severity">By Severity</Option>
              <Option value="status">By Status</Option>
            </Select>
          </Space>
          <Collapse defaultActiveKey={Object.keys(groupedControls)}>
            {Object.entries(groupedControls).map(([groupName, groupControls]) => (
              <Panel
                key={groupName}
                header={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'bold' }}>{groupName}</span>
                    <Tag color={colors.primary[500]}>{groupControls.length} controls</Tag>
                  </div>
                }
              >
                <TGDataTable
                  data={groupControls}
                  columns={columns}
                  rowKey="code"
                  loading={false}
                  pagination="none"
                  showExport={false}
                  showColumnConfig={false}
                  showFilters={false}
                  rowSelection={rowSelection}
                  onRow={(record) => ({
                    onClick: (e) => {
                      if (e.target.type !== 'checkbox') {
                        navigate(`/trustgrid/controls/${record.code}`);
                      }
                    },
                    style: { cursor: 'pointer' }
                  })}
                />
              </Panel>
            ))}
          </Collapse>
        </Card>
      )}
    </TrustGridPage>
  );
}

export default Controls;
